/**
 * Conversation limit ("modo cola") — normalizers for the backend responses
 * (spec §5). Fail-closed towards "feature off": any malformed payload becomes
 * the disabled view, so a bad response can never change the chat (R8).
 */
import { CAPACITY_STATUS_VALUES, deriveStatus } from './capacity';
import { LIMIT_MIN, LIMIT_MAX, AGENT_LIMIT_MAX, IDLE_MIN, IDLE_MAX, DEFAULT_LIMIT, DEFAULT_IDLE_MINUTES } from './configValidation';

export const DISABLED_VIEW = Object.freeze({ enabled: false });

/**
 * The request failed (404, network, 5xx). Behaves as disabled (R8), but lets
 * the heartbeat keep the last known state instead of switching the feature
 * off for the whole session because of one network blip.
 */
export const FAILED_VIEW = Object.freeze({ enabled: false, failed: true });

const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const isNonNegativeInt = (value) => Number.isInteger(value) && value >= 0;
const isIntInRange = (value, min, max) => Number.isInteger(value) && value >= min && value <= max;
const stringOrNull = (value) => (typeof value === 'string' ? value : null);

const normalizeSeat = (seat) => ({
  clientId: seat.clientId,
  seatIndex: isNonNegativeInt(seat.seatIndex) ? seat.seatIndex : null,
  overflow: seat.overflow === true,
  takenAt: stringOrNull(seat.takenAt),
});

const normalizeSeats = (seats) => {
  if (!Array.isArray(seats)) return [];
  const seen = new Set();
  return seats
    .filter((seat) => isObject(seat) && typeof seat.clientId === 'string' && seat.clientId)
    .filter((seat) => {
      if (seen.has(seat.clientId)) return false;
      seen.add(seat.clientId);
      return true;
    })
    .map(normalizeSeat);
};

const statusOf = (status, activeCount, limit) =>
  CAPACITY_STATUS_VALUES.includes(status) ? status : deriveStatus(activeCount, limit);

/** `CapacityView` from `/me`, `/fill` and `/release`. */
export const normalizeCapacityView = (raw) => {
  if (!isObject(raw) || raw.enabled !== true) return DISABLED_VIEW;
  const { limit, activeCount } = raw;
  if (!Number.isInteger(limit) || limit < 1 || !isNonNegativeInt(activeCount)) return DISABLED_VIEW;
  return {
    enabled: true,
    limit,
    activeCount,
    availableSlots: isNonNegativeInt(raw.availableSlots)
      ? raw.availableSlots
      : Math.max(0, limit - activeCount),
    status: statusOf(raw.status, activeCount, limit),
    seats: normalizeSeats(raw.seats),
    queueWaiting: isNonNegativeInt(raw.queueWaiting) ? raw.queueWaiting : 0,
  };
};

const EMPTY_LOAD = Object.freeze({
  enabled: false,
  queue: Object.freeze({ waiting: 0, oldestEnqueuedAt: null }),
  agents: Object.freeze([]),
});

const normalizeAgentRow = (row) => {
  const limit = Number.isInteger(row.limit) && row.limit > 0 ? row.limit : null;
  const activeCount = isNonNegativeInt(row.activeCount) ? row.activeCount : 0;
  return {
    id: row.id,
    name: typeof row.name === 'string' && row.name.trim() ? row.name : 'Sin nombre',
    limit,
    activeCount,
    status: statusOf(row.status, activeCount, limit),
    lastSeenAt: stringOrNull(row.lastSeenAt),
  };
};

/** `GET /conversation-limit/load` (admin panel). */
export const normalizeLoad = (raw) => {
  if (!isObject(raw) || raw.enabled !== true) {
    return { enabled: false, queue: { ...EMPTY_LOAD.queue }, agents: [] };
  }
  const queue = isObject(raw.queue) ? raw.queue : {};
  const agents = Array.isArray(raw.agents) ? raw.agents : [];
  return {
    enabled: true,
    queue: {
      waiting: isNonNegativeInt(queue.waiting) ? queue.waiting : 0,
      oldestEnqueuedAt: stringOrNull(queue.oldestEnqueuedAt),
    },
    agents: agents
      .filter((row) => isObject(row) && typeof row.id === 'string' && row.id)
      .map(normalizeAgentRow),
  };
};

const normalizeAgentLimits = (raw) => {
  if (!isObject(raw)) return {};
  return Object.keys(raw).reduce((acc, userId) => {
    if (isIntInRange(raw[userId], LIMIT_MIN, AGENT_LIMIT_MAX)) acc[userId] = raw[userId];
    return acc;
  }, {});
};

const normalizeIdle = (value) => {
  if (value === null) return null;
  return isIntInRange(value, IDLE_MIN, IDLE_MAX) ? value : DEFAULT_IDLE_MINUTES;
};

/** `GET/PUT /conversation-limit/config` (admin). */
export const normalizeConfig = (raw) => {
  const source = isObject(raw) ? raw : {};
  return {
    enabled: source.enabled === true,
    defaultLimit: isIntInRange(source.defaultLimit, LIMIT_MIN, LIMIT_MAX) ? source.defaultLimit : DEFAULT_LIMIT,
    agentLimits: normalizeAgentLimits(source.agentLimits),
    idleReleaseMinutes: normalizeIdle(source.idleReleaseMinutes),
  };
};
